import { createRndApi } from '@frontend/api-wrapper';
import {
  NEW_API_TOKEN,
  NEW_API_URL,
  RND_API_TOKEN,
  RND_API_URL,
} from '../constants/common';

export const RndApi = createRndApi({
  baseUrl: RND_API_URL,
  api_token: RND_API_TOKEN,
});

export const NewApi = createRndApi({
  baseUrl: NEW_API_URL,
  api_token: NEW_API_TOKEN,
});

/**
 * Proxy utility for API calls that tries old backend first,
 * then falls back to new backend if needed
 * @param {Function} oldApiCall - Function that calls the old API
 * @param {Function} newApiCall - Function that calls the new API
 * @returns {Promise} - Promise resolving to API response
 */
export const proxyApiCall = async (oldApiCall, newApiCall) => {
  try {
    const oldResponse = await oldApiCall();

    return oldResponse;
  } catch (oldError) {
    try {
      const newResponse = await newApiCall();

      return newResponse;
    } catch (newError) {
      throw newError;
    }
  }
};

export const ProxyApi = new Proxy(
  {},
  {
    get: (target, prop) => {
      if (
        typeof RndApi[prop] === 'function' &&
        typeof NewApi[prop] === 'function'
      ) {
        return (...args) => {
          return proxyApiCall(
            () => RndApi[prop](...args),
            () => NewApi[prop](...args)
          );
        };
      }

      return undefined;
    },
  }
);

/**
 * Proxy utility for direct fetch API calls that tries old backend first,
 * then falls back to new backend if needed
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise} - Promise resolving to API response
 */
export const proxyFetch = async (endpoint, options = {}) => {
  try {
    const oldUrl = `${RND_API_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}api_token=${RND_API_TOKEN}`;
    const oldResponse = await fetch(oldUrl, options);

    if (!oldResponse.ok) {
      throw new Error(`Old API responded with status: ${oldResponse.status}`);
    }

    return await oldResponse.json();
  } catch (oldError) {
    try {
      const newUrl = `${NEW_API_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}api_token=${NEW_API_TOKEN}`;
      const newResponse = await fetch(newUrl, options);

      if (!newResponse.ok) {
        throw new Error(`New API responded with status: ${newResponse.status}`);
      }

      return await newResponse.json();
    } catch (newError) {
      console.error('Both API calls failed:', oldError, newError);
      throw newError;
    }
  }
};
